import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header';
import SearchPalette from './components/SearchPalette';
import Footer from './components/Footer';
import Login from './components/Login';
import Register from './components/Register';
import CartDrawer from './components/shop/CartDrawer';
import Shop from './components/shop/Shop';
import ProductDetail from './components/shop/ProductDetail';
import Checkout from './components/shop/Checkout';
import Home from './components/Home';
import BrandCategory from './components/BrandCategory';
import AdminDashboard from './components/AdminDashboard';
import CustomerShop from './components/CustomerShop';
import CustomerDashboard from './components/CustomerDashboard';
import PublicPage from './components/PublicPage';
import DealerLocator from './components/DealerLocator';
import Blog from './components/Blog';
import BlogDetail from './components/BlogDetail';
import Distributors from './components/Distributors';
import ChannelRegistration from './components/ChannelRegistration';
import Careers from './components/Careers';
import LocalSpotlight from './components/LocalSpotlight';
import Profile from './components/Profile';
import ResetPassword from './components/ResetPassword';
import './index.css';

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const isAdmin = user?.roles?.some((role) => ['super_admin', 'sub_admin'].includes(role.slug));

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return isAdmin ? children : <Navigate to="/profile" />;
}

function AppShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const location = useLocation();
  const hideChrome = location.pathname.startsWith('/admin');

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {!hideChrome && (
        <Header 
          onOpenSearch={() => setSearchOpen(true)} 
          onOpenCart={() => setCartOpen(true)} 
        />
      )}
      <main className="flex-1">
        <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<PublicPage slugOverride="about" />} />
              <Route path="/investors" element={<PublicPage slugOverride="investors" />} />
              <Route path="/brands/:slug" element={<BrandCategory />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/:slug" element={<ProductDetail />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route
                path="/customer-dashboard"
                element={
                  <ProtectedRoute>
                    <CustomerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/dealers" element={<DealerLocator />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogDetail />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/local-spotlight" element={<LocalSpotlight />} />
              <Route path="/distributors" element={<Distributors />} />
              <Route path="/channel" element={<ChannelRegistration />} />
              <Route path="/investments" element={<PublicPage slugOverride="investments" />} />
              <Route path="/contact" element={<PublicPage slugOverride="contact" />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route path="/:slug" element={<PublicPage />} />
              <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </main>
      {!hideChrome && <Footer />}
      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppShell />
      </Router>
    </AuthProvider>
  );
}

export default App;
